import { useState, type ChangeEvent, type FormEvent } from 'react';
import { User, Camera, Building, Mail, Lock, Eye, EyeOff, Save, Loader2, CheckCircle, AlertCircle, Trash2 } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuth } from '../context/AuthContext';
import { useCompany } from '../context/CompanyContext';
import { fileToDataUrl, createThumbnail, cn } from '../utils/formatters';
import { input, label } from '../styles/components';
import type { User as UserModel, Company } from '../types';

const profileSchema = z.object({
  name: z.string().min(2, 'Mínimo 2 caracteres'),
  email: z.string().email('Email inválido'),
  currentPassword: z.string().optional(),
  newPassword: z.string().min(8, 'Mínimo 8 caracteres').optional().or(z.literal('')),
  confirmPassword: z.string().optional(),
}).refine(data => !data.newPassword || data.newPassword === data.confirmPassword, {
  message: 'Las contraseñas no coinciden',
  path: ['confirmPassword'],
}).refine(data => !data.newPassword || data.currentPassword, {
  message: 'Debes ingresar tu contraseña actual para cambiarla',
  path: ['currentPassword'],
});

type ProfileForm = z.infer<typeof profileSchema>;

export function ProfilePage() {
  const { user, updateUser, changePassword } = useAuth();
  const { company, updateCompany } = useCompany();
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [activeTab, setActiveTab] = useState<'profile' | 'company'>('profile');

  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ProfileForm>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      name: user?.name || '',
      email: user?.email || '',
    },
  });

  const newPassword = watch('newPassword');

  const handleAvatarChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setMessage({ type: 'error', text: 'El archivo debe ser una imagen' });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setMessage({ type: 'error', text: 'La imagen no debe superar 5MB' });
      return;
    }
    const dataUrl = await fileToDataUrl(file);
    const thumb = await createThumbnail(dataUrl, 200);
    setAvatarPreview(thumb);
    setMessage({ type: 'success', text: 'Avatar actualizado. Guarda los cambios para confirmar.' });
  };

  const handleLogoChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setMessage({ type: 'error', text: 'El archivo debe ser una imagen' });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setMessage({ type: 'error', text: 'La imagen no debe superar 5MB' });
      return;
    }
    const dataUrl = await fileToDataUrl(file);
    const thumb = await createThumbnail(dataUrl, 300);
    setLogoPreview(thumb);
    setMessage({ type: 'success', text: 'Logo actualizado. Guarda los cambios para confirmar.' });
  };

  const onSubmit = async (data: ProfileForm) => {
    setIsSaving(true);
    setMessage(null);

    try {
      if (data.newPassword) {
        const result = await changePassword(user?.email ?? '', data.currentPassword ?? '', data.newPassword);
        if (!result.success) {
          setMessage({ type: 'error', text: result.error || 'No se pudo cambiar la contraseña' });
          return;
        }
      }

      const updates: Partial<UserModel> = {
        name: data.name,
        email: data.email,
      };

      if (avatarPreview) {
        updates.avatar = avatarPreview;
      }

      await updateUser(updates);
      setMessage({
        type: 'success',
        text: data.newPassword ? 'Perfil y contraseña actualizados correctamente' : 'Perfil actualizado correctamente',
      });
      reset({ name: data.name, email: data.email });
    } catch (error) {
      setMessage({ type: 'error', text: 'Error al actualizar el perfil' });
    } finally {
      setIsSaving(false);
    }
  };

  const onCompanySubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    setMessage(null);

    try {
      const updates: Partial<Company> = {};
      if (logoPreview) {
        updates.logo = logoPreview;
      }
      // Otros campos se pueden agregar aquí
      await updateCompany!(updates);
      setMessage({ type: 'success', text: 'Empresa actualizada correctamente' });
    } catch (error) {
      setMessage({ type: 'error', text: 'Error al actualizar la empresa' });
    } finally {
      setIsSaving(false);
    }
  };

  const removeAvatar = () => {
    setAvatarPreview(null);
    setMessage({ type: 'success', text: 'Avatar eliminado. Guarda los cambios para confirmar.' });
  };

  const removeLogo = () => {
    setLogoPreview(null);
    setMessage({ type: 'success', text: 'Logo eliminado. Guarda los cambios para confirmar.' });
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div className="space-y-1">
          <span className="b2b-eyebrow">Perfil</span>
          <h1 className="text-3xl font-extrabold tracking-tight">Mi Perfil</h1>
          <p className="b2b-muted">Gestiona tu información personal y de la empresa</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-[var(--b2b-border)]">
        <button
          onClick={() => setActiveTab('profile')}
          className={cn(
            'px-4 py-3 text-sm font-medium border-b-2 transition-colors inline-flex items-center gap-2',
            activeTab === 'profile'
              ? 'border-[color:var(--b2b-accent)] text-[color:var(--b2b-accent-strong)]'
              : 'border-transparent b2b-muted'
          )}
        >
          <User className="w-4 h-4" />
          Perfil Personal
        </button>
        {user?.role === 'admin' || user?.role === 'superadmin' ? (
          <button
            onClick={() => setActiveTab('company')}
            className={cn(
              'px-4 py-3 text-sm font-medium border-b-2 transition-colors inline-flex items-center gap-2',
              activeTab === 'company'
                ? 'border-[color:var(--b2b-accent)] text-[color:var(--b2b-accent-strong)]'
                : 'border-transparent b2b-muted'
            )}
          >
            <Building className="w-4 h-4" />
            Mi Empresa
          </button>
        ) : null}
      </div>

      {message && (
        <div className={cn('flex items-center gap-3 p-4 rounded-lg', message.type === 'success' ? 'b2b-pill b2b-pill--ok' : 'b2b-pill b2b-pill--danger')} role="alert">
          {message.type === 'success' ? <CheckCircle className="w-5 h-5" /> : <AlertCircle className="w-5 h-5" />}
          <span>{message.text}</span>
        </div>
      )}

      {activeTab === 'profile' && (
        <form onSubmit={handleSubmit(onSubmit)} className="b2b-panel p-6 space-y-6" noValidate>
          {/* Avatar */}
          <div className="flex items-center gap-6">
            <div className="relative">
              <div className="w-24 h-24 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center overflow-hidden border-4 border-white dark:border-secondary-900 shadow-lg">
                {avatarPreview ? (
                  <img src={avatarPreview} alt="Avatar" className="w-full h-full object-cover" />
                ) : user?.avatar ? (
                  <img src={user.avatar} alt="Avatar" className="w-full h-full object-cover" />
                ) : (
                  <User className="w-10 h-10 text-primary-600 dark:text-primary-400" />
                )}
              </div>
              <label className="absolute bottom-0 right-0 w-8 h-8 rounded-full bg-primary-600 text-white flex items-center justify-center cursor-pointer hover:bg-primary-700 transition-colors border-2 border-white dark:border-secondary-900"
                aria-label="Cambiar avatar">
                <Camera className="w-4 h-4" />
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleAvatarChange}
                  className="sr-only"
                  id="avatar-upload"
                />
              </label>
              {avatarPreview && (
                <button
                  type="button"
                  onClick={removeAvatar}
                  className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-danger-500 text-white flex items-center justify-center hover:bg-danger-600 transition-colors"
                  aria-label="Eliminar avatar"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              )}
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-secondary-900 dark:text-white">Foto de perfil</h3>
              <p className="text-sm text-secondary-500">JPG, PNG o WebP. Máximo 5MB. Se recortará a cuadrado.</p>
            </div>
          </div>

          <hr className="border-secondary-200 dark:border-secondary-700" />

          {/* Datos personales */}
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="name" className={label}>Nombre completo</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-secondary-400" />
                <input
                  {...register('name')}
                  id="name"
                  type="text"
                  className={cn(input, 'pl-10')}
                  disabled={isSaving}
                  aria-invalid={!!errors.name}
                />
              </div>
              {errors.name && <p className="mt-1 text-sm text-danger-600">{errors.name.message}</p>}
            </div>
            <div>
              <label htmlFor="email" className={label}>Correo electrónico</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-secondary-400" />
                <input
                  {...register('email')}
                  id="email"
                  type="email"
                  className={cn(input, 'pl-10')}
                  disabled={isSaving}
                  aria-invalid={!!errors.email}
                />
              </div>
              {errors.email && <p className="mt-1 text-sm text-danger-600">{errors.email.message}</p>}
            </div>
          </div>

          <hr className="border-secondary-200 dark:border-secondary-700" />

          {/* Cambio de contraseña */}
          <div>
            <h3 className="text-lg font-semibold text-secondary-900 dark:text-white mb-4">Cambiar contraseña</h3>
            <p className="text-sm text-secondary-500 mb-4">Deja los campos vacíos si no quieres cambiar la contraseña.</p>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label htmlFor="currentPassword" className={label}>Contraseña actual</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-secondary-400" />
                  <input
                    {...register('currentPassword')}
                    id="currentPassword"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    className={cn(input, 'pl-10 pr-10')}
                    disabled={isSaving}
                    aria-invalid={!!errors.currentPassword}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-secondary-400"
                    aria-label={showPassword ? 'Ocultar' : 'Mostrar'}
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                {errors.currentPassword && <p className="mt-1 text-sm text-danger-600">{errors.currentPassword.message}</p>}
              </div>
              <div>
                <label htmlFor="newPassword" className={label}>Nueva contraseña</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-secondary-400" />
                  <input
                    {...register('newPassword')}
                    id="newPassword"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    className={cn(input, 'pl-10')}
                    disabled={isSaving}
                    aria-invalid={!!errors.newPassword}
                  />
                </div>
                {errors.newPassword && <p className="mt-1 text-sm text-danger-600">{errors.newPassword.message}</p>}
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="confirmPassword" className={label}>Confirmar nueva contraseña</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-secondary-400" />
                  <input
                    {...register('confirmPassword')}
                    id="confirmPassword"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    className={cn(input, 'pl-10')}
                    disabled={isSaving}
                    aria-invalid={!!errors.confirmPassword}
                  />
                </div>
                {errors.confirmPassword && <p className="mt-1 text-sm text-danger-600">{errors.confirmPassword.message}</p>}
              </div>
            </div>
          </div>

          {/* Botones */}
          <div className="flex flex-col sm:flex-row gap-3 justify-end pt-4 border-t border-[var(--b2b-border)]">
            <button type="button" onClick={() => reset({ name: user?.name || '', email: user?.email || '' })} className="b2b-btn b2b-btn--ghost">
              Cancelar
            </button>
            <button type="submit" className="b2b-btn b2b-btn--primary disabled:opacity-50" disabled={isSaving || isSubmitting}>
              {isSaving ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Guardando...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Save className="w-4 h-4" />
                  Guardar cambios
                </span>
              )}
            </button>
          </div>
        </form>
      )}

      {activeTab === 'company' && company && (
        <form onSubmit={onCompanySubmit} className="b2b-panel p-6 space-y-6" noValidate>
          {/* Logo de la empresa */}
          <div className="flex items-start gap-6">
            <div className="relative">
              <div className="w-32 h-32 rounded-xl bg-secondary-100 dark:bg-secondary-800 flex items-center justify-center overflow-hidden border-2 border-secondary-200 dark:border-secondary-700">
                {logoPreview ? (
                  <img src={logoPreview} alt="Logo empresa" className="w-full h-full object-contain p-2" />
                ) : company?.logo ? (
                  <img src={company.logo} alt="Logo empresa" className="w-full h-full object-contain p-2" />
                ) : (
                  <Building className="w-12 h-12 text-secondary-400 dark:text-secondary-500" />
                )}
              </div>
              <label className="absolute bottom-0 left-1/2 -translate-x-1/2 w-8 h-8 rounded-full bg-primary-600 text-white flex items-center justify-center cursor-pointer hover:bg-primary-700 transition-colors border-2 border-white dark:border-secondary-900"
                aria-label="Cambiar logo">
                <Camera className="w-4 h-4" />
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleLogoChange}
                  className="sr-only"
                  id="logo-upload"
                />
              </label>
              {(logoPreview || company?.logo) && (
                <button
                  type="button"
                  onClick={removeLogo}
                  className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-danger-500 text-white flex items-center justify-center hover:bg-danger-600 transition-colors"
                  aria-label="Eliminar logo"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              )}
            </div>
            <div className="flex-1 pt-2">
              <h3 className="text-lg font-semibold text-secondary-900 dark:text-white">Logotipo de la empresa</h3>
              <p className="text-sm text-secondary-500">Este logo aparecerá en todos los presupuestos generados. Formatos: JPG, PNG, WebP. Máximo 5MB. Se recomienda fondo transparente.</p>
            </div>
          </div>

          <hr className="border-secondary-200 dark:border-secondary-700" />

          {/* Datos fiscales */}
          <h3 className="text-lg font-semibold text-secondary-900 dark:text-white">Datos fiscales</h3>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="companyName" className={label}>Nombre comercial</label>
              <input id="companyName" type="text" className={input} value={company.name} readOnly />
              <p className="mt-1 text-xs text-secondary-500">Se define al registrarse. Contacta con soporte para cambiarlo.</p>
            </div>
            <div>
              <label htmlFor="fiscalName" className={label}>Razón social</label>
              <input id="fiscalName" type="text" className={input} value={company.fiscalName || ''} readOnly />
            </div>
            <div>
              <label htmlFor="cif" className={label}>CIF / NIF</label>
              <input id="cif" type="text" className={input} value={company.cif || ''} readOnly />
            </div>
            <div>
              <label htmlFor="vatRate" className={label}>IVA por defecto</label>
              <select id="vatRate" className={input} disabled>
                <option value="21">{company.vatRate === 21 ? '21% (General)' : '21% (General)'}</option>
                <option value="10">{company.vatRate === 10 ? '10% (Reducido - Rehabilitación)' : '10% (Reducido - Rehabilitación)'}</option>
              </select>
            </div>
            <div className="sm:col-span-2">
              <label htmlFor="address" className={label}>Dirección fiscal</label>
              <textarea id="address" className={cn(input, 'min-h-[100px]')} readOnly>{company.address || ''}</textarea>
            </div>
            <div>
              <label htmlFor="phone" className={label}>Teléfono</label>
              <input id="phone" type="tel" className={input} value={company.phone || ''} readOnly />
            </div>
            <div>
              <label htmlFor="email" className={label}>Email de la empresa</label>
              <input id="email" type="email" className={input} value={company.email || ''} readOnly />
            </div>
          </div>

          <hr className="border-secondary-200 dark:border-secondary-700" />

          {/* Textos legales en presupuestos */}
          <h3 className="text-lg font-semibold text-secondary-900 dark:text-white">Textos legales en presupuestos</h3>
          <div className="space-y-4">
            <div>
              <label htmlFor="paymentTerms" className={label}>Condiciones de pago</label>
              <textarea
                id="paymentTerms"
                className={cn(input, 'min-h-[80px]')}
                readOnly
                defaultValue={company.paymentTerms || 'Pago 50% a la firma del presupuesto y 50% a la finalización de la obra.'}
              />
            </div>
            <div>
              <label htmlFor="warrantyClauses" className={label}>Cláusulas de garantía</label>
              <textarea
                id="warrantyClauses"
                className={cn(input, 'min-h-[80px]')}
                readOnly
                defaultValue={company.warrantyClauses || 'Garantía de 12 meses en mano de obra y materiales según Ley de Ordenación de la Edificación.'}
              />
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 justify-end pt-4 border-t border-[var(--b2b-border)]">
            <button type="submit" className="b2b-btn b2b-btn--primary disabled:opacity-50" disabled={isSaving}>
              {isSaving ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Guardando...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Save className="w-4 h-4" />
                  Guardar cambios de empresa
                </span>
              )}
            </button>
          </div>
        </form>
      )}
    </div>
  );
}